import React, { useState } from "react";
import { useObserver } from "mobx-react-lite";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    TablePagination,
    MenuItem,
    makeStyles,
    Theme,
    createStyles,
    TableFooter,
} from "@material-ui/core";
import LockIcon from "@material-ui/icons/Lock";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

// Core Components
import { Loader } from "Core/Components/Loader";
import { Error } from "Custom/Components/Error";

// Custom ViewModels
import { SuperAdminViewModel } from "Custom/ViewModels/SuperAdminViewModel";

// Custom Styles
import { DashboardPageStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";

// Enums and Models
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";
import { useSuperAdminStyles } from "Custom/StylesAppSpecific/SuperAdminPageStyling";

import { EditableInput } from "Custom/Components/Edits/EditableInput";
import { KeyValuePair } from "Core/Models/KeyValuePair";
import { generateID } from "Core/Utils/Utils";

export const SuperAdminView: React.FC = () => {
    const adminStyles = useSuperAdminStyles();
    const dashboardPageStyles = DashboardPageStyles();

    const [viewModel] = useState(() => new SuperAdminViewModel());
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const renderBusy = () => <Loader />;

    const renderError = () => (
        <Box style={{ margin: 24 }}>
            <Error errorText={"Failed to retrieve information."} />
            <Button variant="contained" color="primary" onClick={viewModel.loadCustomers} style={{ marginTop: 16 }}>
                Retry
            </Button>
        </Box>
    );

    const renderContent = () => {
        const groupedCustomers = viewModel.groupedCustomers;
        const paginatedGroupedCustomers = groupedCustomers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
        const developmentItems: KeyValuePair[] = [{ key: "All", value: "ALL" }];
        const houseTypeItems: KeyValuePair[] = [{ key: "All", value: "ALL" }];
        const customerItems: KeyValuePair[] = [{ key: "All", value: "ALL" }];

        Array.from(new Set(viewModel.customers.map(c => c.developmentName)))
            .sort()
            .forEach(dev => {
                developmentItems.push({ key: dev, value: dev });
            });

        Array.from(new Set(viewModel.customers.map(c => c.houseTypeName)))
            .sort()
            .forEach(houseType => {
                houseTypeItems.push({ key: houseType, value: houseType });
            });

        Array.from(new Set(viewModel.customers.map(c => c.customerName)))
            .sort()
            .forEach(customer => {
                customerItems.push({ key: customer, value: customer });
            });

        return (
            <Box className={adminStyles.root}>
                {/* Filters */}
                <Box className={adminStyles.filtersContainer}>
                    <EditableInput
                        className={`${adminStyles.formControl}`}
                        fieldName="customerFilter"
                        inputProps={{
                            displayEmpty: true,
                        }}
                        label="Customer"
                        selectItems={customerItems}
                        type="select"
                        viewModel={viewModel}
                        onChange={e => viewModel.setCustomerFilter(e.target.value)}
                    />

                    <EditableInput
                        className={adminStyles.formControl}
                        fieldName={"plotFilter"}
                        inputProps={{
                            multiline: false,
                            placeholder: "0",
                            InputProps: {
                                notched: false,
                            },
                            InputLabelProps: {
                                shrink: true,
                            },
                        }}
                        validateOnBlur
                        label="Plot No."
                        viewModel={viewModel}
                    />

                    <EditableInput
                        className={`${adminStyles.formControl}`}
                        fieldName="houseTypeFilter"
                        inputProps={{
                            displayEmpty: true,
                        }}
                        label="House type"
                        selectItems={houseTypeItems}
                        type="select"
                        viewModel={viewModel}
                        onChange={e => viewModel.setHouseTypeFilter(e.target.value)}
                    />

                    <EditableInput
                        className={`${adminStyles.formControl}`}
                        fieldName="developmentFilter"
                        inputProps={{
                            displayEmpty: true,
                        }}
                        label="Development"
                        selectItems={developmentItems}
                        type="select"
                        viewModel={viewModel}
                        onChange={e => viewModel.setDevelopmentFilter(e.target.value)}
                    />

                    <Button className={adminStyles.reportBtn} variant="contained" color="primary" onClick={viewModel.clearFilters}>
                        Clear All Filters
                    </Button>
                </Box>

                <TableContainer key={generateID() + "tableContainer"} component={Paper} className={adminStyles.tableContainer}>
                    <Table key={generateID() + "table"}>
                        {/* Header */}
                        <TableHead className={adminStyles.tableHeader}>
                            <TableRow>
                                <TableCell>
                                    <div>
                                        Customer <ExpandMoreIcon style={{ fontSize: 16, marginLeft: 4 }} />
                                    </div>
                                </TableCell>
                                <TableCell className={adminStyles.lockCol}>
                                    {" "}
                                    <div></div>
                                </TableCell>
                                <TableCell>
                                    {" "}
                                    <div>Phone number</div>
                                </TableCell>
                                <TableCell>
                                    {" "}
                                    <div>Email address</div>
                                </TableCell>
                                <TableCell>
                                    <div>
                                        Plot(s) <ExpandMoreIcon style={{ fontSize: 16, marginLeft: 4 }} />
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div>
                                        House type <ExpandMoreIcon style={{ fontSize: 16, marginLeft: 4 }} />
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div>Development</div>
                                </TableCell>
                                <TableCell>
                                    <div>Development address</div>
                                </TableCell>
                                <TableCell>
                                    <div></div>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        {/* Body */}
                        <TableBody>
                            {paginatedGroupedCustomers.map((group, groupIndex) =>
                                group.properties.map((property, propertyIndex) => (
                                    <TableRow
                                        key={`${generateID()}-${group.customer.customerId}-${property.propertyId}`}
                                        style={{
                                            backgroundColor: groupIndex % 2 === 0 ? "#ECECEC" : "#fff",
                                        }}
                                    >
                                        {propertyIndex === 0 && (
                                            <>
                                                <TableCell rowSpan={group.properties.length} style={{ verticalAlign: "top", fontWeight: 400, color: "#3C3C3B" }}>
                                                    {group.customer.customerName}
                                                </TableCell>
                                                <TableCell
                                                    rowSpan={group.properties.length}
                                                    style={{
                                                        width: "40px",
                                                        textAlign: "center",
                                                        padding: "0 8px",
                                                        verticalAlign: "top",
                                                    }}
                                                >
                                                    <LockIcon style={{ fontSize: 18, color: "#555" }} />
                                                </TableCell>
                                                <TableCell rowSpan={group.properties.length} style={{ verticalAlign: "top", fontWeight: 400, color: "#3C3C3B" }}>
                                                    {group.customer.phoneNumber}
                                                </TableCell>
                                                <TableCell rowSpan={group.properties.length} style={{ verticalAlign: "top" }}>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            color: "#006FFF",
                                                            textDecoration: "underline",
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={() => window.open(`mailto:${group.customer.emailAddress}`)}
                                                    >
                                                        {group.customer.emailAddress}
                                                    </Typography>
                                                </TableCell>
                                            </>
                                        )}
                                        <TableCell style={{ textAlign: "center" }}>{property.plotNumber}</TableCell>
                                        <TableCell>{property.houseTypeName}</TableCell>
                                        <TableCell>{property.developmentName}</TableCell>
                                        <TableCell>{property.developmentAddress}</TableCell>
                                        <TableCell>
                                            <IconButton
                                                className={adminStyles.actionButton}
                                                onClick={() => viewModel.handleViewCustomer(property)}
                                                title="View Customer Dashboard"
                                                style={{ padding: 4 }}
                                            >
                                                <ArrowForwardIcon style={{ fontSize: 16 }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                )),
                            )}
                        </TableBody>

                        {/* Footer with pagination */}
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={[10, 25, 50]}
                                    count={groupedCustomers.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onChangePage={handleChangePage}
                                    onChangeRowsPerPage={handleChangeRowsPerPage}
                                    labelRowsPerPage="Show:"
                                    labelDisplayedRows={({ page }) => `Page ${page + 1} of ${Math.ceil(groupedCustomers.length / rowsPerPage)}`}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    return useObserver(() => (
        <Box className={`${dashboardPageStyles.root} ${adminStyles.root}`}>
            <Box className={dashboardPageStyles.workspace} style={{ width: "100%" }}>
                {viewModel.ApiStatus === ApiStatus.success && <React.Fragment>{renderContent()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.loading && <React.Fragment>{renderBusy()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.error && <React.Fragment>{renderError()}</React.Fragment>}
            </Box>
        </Box>
    ));
};
